import { useCallback, useEffect, useRef, useState } from "react";
import { RotateCw } from "lucide-react";
import { Camera, X, RotateCcw, Loader2, AlertCircle, Star, ChevronLeft, ChevronRight, GripVertical, FileText } from "lucide-react";
import { toast } from "sonner";
import { uploadPhoto, deleteDraftMedia, fetchDraftMediaBlob, rotateDraftMedia, saveMediaOrder, scanDraftPaper, errMsg } from "@/lib/applyApi";
import { DropZone, splitFiles } from "@/components/apply/DropZone";
import { VideoUploader } from "@/components/video/VideoUploader";

const CONCURRENCY = 2;
const PHOTO_EXTS = ["jpg", "jpeg", "png", "heic", "heif", "webp"];
const PHOTO_ACCEPT = "image/jpeg,image/png,image/heic,image/heif,image/webp,.jpg,.jpeg,.png,.heic,.heif,.webp";

export function BlobImg({ load, alt, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let live = true;
    load().then((b) => { if (live) { url = URL.createObjectURL(b); setSrc(url); } }).catch(() => {});
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [load]);
  return src ? <img src={src} alt={alt} draggable={false} className={className} /> : <div className={`${className} bg-stone-100 animate-pulse`} />;
}

const iconBtn = "h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center disabled:opacity-30";

function Tile({ testId, children, onRemove, removing, ...rest }) {
  return (
    <div data-testid={testId} {...rest} className={`relative aspect-square rounded-lg overflow-hidden border bg-stone-50 ${rest.className || "border-stone-200"}`}>
      {children}
      {onRemove && (
        <button type="button" aria-label="Remove photo" data-testid={`${testId}-remove`} onClick={onRemove} disabled={removing}
          className={`absolute top-1 right-1 ${iconBtn} h-11 w-11 sm:h-9 sm:w-9`}>
          {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}

function PhotoTile({ m, i, total, isMain, app, token, drag, onMove, onMain, onRemove, removing, onRotated }) {
  const load = useCallback(() => fetchDraftMediaBlob(app.id, token, m.id, m.rotation), [app.id, token, m.id, m.rotation]);
  const [rotating, setRotating] = useState(false);
  const rotate = async (dir) => {
    setRotating(true);
    try { onRotated((await rotateDraftMedia(app.id, token, m.id, dir)).rotation); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setRotating(false); }
  };
  return (
    <Tile testId={`photo-${m.id}`} draggable onDragStart={(e) => drag.start(e, i)} onDragOver={(e) => drag.over(e, i)}
      onDrop={(e) => drag.drop(e, i)} onDragEnd={drag.end} onRemove={onRemove} removing={removing}
      className={`cursor-grab active:cursor-grabbing ${drag.target === i ? "border-amber-700 ring-2 ring-amber-500" : isMain ? "border-amber-600 ring-2 ring-amber-400" : "border-stone-200"}`}>
      <BlobImg load={load} alt={m.original_filename} className="h-full w-full object-cover pointer-events-none" />
      <span className="absolute top-1 left-1 h-6 min-w-6 px-1.5 rounded-full bg-black/60 text-white text-[11px] font-bold flex items-center justify-center gap-0.5">
        <GripVertical className="h-3 w-3 hidden md:block" />{i + 1}
      </span>
      <div className="absolute top-14 sm:top-11 right-1 flex flex-col gap-1">
        <button type="button" aria-label="Rotate left" data-testid={`photo-rotate-ccw-${m.id}`} disabled={rotating} onClick={() => rotate("ccw")} className={iconBtn}><RotateCcw className="h-4 w-4" /></button>
        <button type="button" aria-label="Rotate right" data-testid={`photo-rotate-cw-${m.id}`} disabled={rotating} onClick={() => rotate("cw")} className={iconBtn}><RotateCw className="h-4 w-4" /></button>
      </div>
      {isMain && <span data-testid={`photo-main-badge-${m.id}`} className="absolute bottom-9 left-1 rounded-full bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5">MAIN</span>}
      <div className="absolute inset-x-0 bottom-0 p-1 flex items-center justify-between bg-gradient-to-t from-black/60 to-transparent">
        <button type="button" aria-label="Move earlier" data-testid={`photo-left-${m.id}`} disabled={i === 0} onClick={() => onMove(i, i - 1)} className={iconBtn}><ChevronLeft className="h-4 w-4" /></button>
        <button type="button" aria-label={isMain ? "Main photo" : "Set as main photo"} aria-pressed={isMain} data-testid={`photo-main-${m.id}`} onClick={onMain}
          className={`${iconBtn} ${isMain ? "bg-amber-500" : ""}`}><Star className={`h-4 w-4 ${isMain ? "fill-white" : ""}`} /></button>
        <button type="button" aria-label="Move later" data-testid={`photo-right-${m.id}`} disabled={i === total - 1} onClick={() => onMove(i, i + 1)} className={iconBtn}><ChevronRight className="h-4 w-4" /></button>
      </div>
    </Tile>
  );
}

// After submission: sellers can still fix photo orientation (order/selection unchanged).
export function SubmittedPhotos({ app, onMediaChange }) {
  const photos = (app.media || []).filter((m) => m.kind === "photo");
  if (!photos.length) return null;
  return (
    <section data-testid="submitted-photos" className="bg-white border border-stone-200 rounded-xl p-4">
      <h2 className="font-semibold text-stone-900 mb-1">Your photos</h2>
      <p className="text-sm text-stone-600 mb-3">Is a photo sideways? Use the rotate buttons. The corrected photo is used in your ad.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {photos.map((m) => <SubmittedPhoto key={m.id} app={app} m={m} onRotated={(r) => onMediaChange((list) => list.map((x) => (x.id === m.id ? { ...x, rotation: r } : x)))} />)}
      </div>
    </section>
  );
}

function SubmittedPhoto({ app, m, onRotated }) {
  const load = useCallback(() => fetchDraftMediaBlob(app.id, null, m.id, m.rotation), [app.id, m.id, m.rotation]);
  const [busy, setBusy] = useState(false);
  const rotate = async (dir) => {
    setBusy(true);
    try { onRotated((await rotateDraftMedia(app.id, null, m.id, dir)).rotation); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`submitted-photo-${m.id}`} className="relative aspect-square rounded-lg overflow-hidden border border-stone-200">
      <BlobImg load={load} alt={m.original_filename} className="h-full w-full object-cover" />
      <div className="absolute inset-x-1 bottom-1 flex justify-between">
        <button type="button" aria-label="Rotate left" data-testid={`submitted-photo-rotate-ccw-${m.id}`} disabled={busy} onClick={() => rotate("ccw")} className="h-10 w-10 rounded-full bg-black/60 text-white flex items-center justify-center disabled:opacity-30"><RotateCcw className="h-4 w-4" /></button>
        <button type="button" aria-label="Rotate right" data-testid={`submitted-photo-rotate-cw-${m.id}`} disabled={busy} onClick={() => rotate("cw")} className="h-10 w-10 rounded-full bg-black/60 text-white flex items-center justify-center disabled:opacity-30"><RotateCw className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function QueueTile({ x, onRetry, onDrop }) {
  return (
    <Tile testId="photo-uploading" onRemove={x.status === "error" ? onDrop : null}>
      <img src={x.preview} alt="" className="h-full w-full object-cover opacity-60" />
      <div className="absolute inset-x-0 bottom-0 p-1.5 bg-black/55 text-white text-[11px]">
        {x.status === "error" ? (
          <div className="space-y-1">
            <div className="flex items-start gap-1"><AlertCircle className="h-3.5 w-3.5 shrink-0" /><span className="line-clamp-2">{x.error}</span></div>
            {!x.tooBig && (
              <button type="button" data-testid="photo-retry" onClick={onRetry} className="w-full h-8 rounded bg-white text-stone-900 font-semibold flex items-center justify-center gap-1">
                <RotateCcw className="h-3.5 w-3.5" /> Retry
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="h-1.5 rounded bg-white/30 overflow-hidden"><div data-testid="photo-progress" className="h-full bg-amber-400 transition-[width]" style={{ width: `${x.progress}%` }} /></div>
            <div className="mt-0.5 truncate">{x.status === "queued" ? "Waiting…" : `${x.progress}%`} · {x.file.name}</div>
          </>
        )}
      </div>
    </Tile>
  );
}

function useTileDrag(onMove) {
  const from = useRef(null);
  const [target, setTarget] = useState(null);
  return {
    target,
    start: (e, i) => { from.current = i; e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); },
    over: (e, i) => { if (from.current === null) return; e.preventDefault(); e.stopPropagation(); setTarget(i); },
    drop: (e, i) => { if (from.current === null) return; e.preventDefault(); e.stopPropagation(); onMove(from.current, i); from.current = null; setTarget(null); },
    end: () => { from.current = null; setTarget(null); },
  };
}

function usePreventWindowDrop() {
  useEffect(() => {
    const guard = (e) => { if (e.dataTransfer?.types?.includes("Files")) e.preventDefault(); };
    window.addEventListener("dragover", guard);
    window.addEventListener("drop", guard);
    return () => { window.removeEventListener("dragover", guard); window.removeEventListener("drop", guard); };
  }, []);
}

function useUploadQueue({ app, token, kind, items, max, maxBytes, noun, onMediaChange, exts = PHOTO_EXTS, extMsg = "Photos must be JPG, JPEG, PNG or HEIC." }) {
  const [queue, setQueue] = useState([]);
  const [notice, setNotice] = useState(null);
  const patch = (key, p) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, ...p } : x)));

  const start = (x) => {
    patch(x.key, { status: "uploading", progress: 0, error: null });
    uploadPhoto(app.id, token, x.file, (p) => patch(x.key, { progress: p }), kind)
      .then((m) => {
        onMediaChange((list) => [...list, m]);
        setQueue((q) => q.filter((y) => y.key !== x.key));
        URL.revokeObjectURL(x.preview);
      })
      .catch((e) => patch(x.key, { status: "error", error: errMsg(e, "Upload failed — check your connection and tap Retry.") }));
  };

  useEffect(() => {
    const running = queue.filter((x) => x.status === "uploading").length;
    queue.filter((x) => x.status === "queued").slice(0, Math.max(0, CONCURRENCY - running)).forEach(start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  const addFiles = (files) => {
    const { ok, error } = splitFiles(files, exts, extMsg);
    const seen = new Set([...items.map((m) => `${m.original_filename}|${m.original_size}`), ...queue.map((x) => `${x.file.name}|${x.file.size}`)]);
    const fresh = ok.filter((f) => !seen.has(`${f.name}|${f.size}`) && seen.add(`${f.name}|${f.size}`));
    const dupes = ok.length - fresh.length;
    const room = Math.max(0, max - items.length - queue.length);
    const msgs = [error, dupes && `${dupes} duplicate photo${dupes > 1 ? "s were" : " was"} skipped.`,
      fresh.length > room && `Only ${max} ${noun} allowed — ${fresh.length - room} not added.`].filter(Boolean);
    setNotice(msgs.length ? msgs.join(" ") : null);
    const add = fresh.slice(0, room).map((file) => {
      const tooBig = file.size > maxBytes;
      return {
        key: `${file.name}-${file.size}-${Math.random()}`, file, preview: URL.createObjectURL(file), progress: 0,
        status: tooBig ? "error" : "queued", error: tooBig ? "Larger than 25 MB — please choose a smaller photo." : null, tooBig,
      };
    });
    setQueue((q) => [...q, ...add]);
  };
  const retry = (key) => patch(key, { status: "queued", error: null });
  const drop = (key) => setQueue((q) => q.filter((y) => y.key !== key));
  return { queue, notice, addFiles, retry, drop, full: items.length + queue.length >= max };
}

function useRemove(app, token, onMediaChange) {
  const [removing, setRemoving] = useState(null);
  const remove = async (mid) => {
    setRemoving(mid);
    try {
      await deleteDraftMedia(app.id, token, mid);
      onMediaChange((list) => list.filter((m) => m.id !== mid));
    } catch (e) {
      toast.error(errMsg(e, "Could not remove photo"));
    } finally {
      setRemoving(null);
    }
  };
  return [removing, remove];
}

export function PapersSection({ app, token, company, onMediaChange, onManual }) {
  const papers = (app.media || []).filter((m) => m.kind === "registration_paper");
  const q = useUploadQueue({ app, token, kind: "registration_paper", items: papers, max: company.max_papers || 10, maxBytes: company.max_photo_bytes, noun: "registration documents", onMediaChange,
    exts: [...PHOTO_EXTS, "pdf"], extMsg: "Registration papers must be JPG, PNG, HEIC or PDF." });
  const [removing, remove] = useRemove(app, token, onMediaChange);
  const tried = useRef(new Set());
  const [scanning, setScanning] = useState(null);
  const setScan = (mid, scan) => onMediaChange((list) => list.map((m) => (m.id === mid ? { ...m, scan } : m)));
  useEffect(() => {
    const nxt = papers.find((m) => !m.scan && !tried.current.has(m.id));
    if (!nxt || scanning) return;
    tried.current.add(nxt.id);
    setScanning(nxt.id);
    scanDraftPaper(app.id, token, nxt.id).then((s) => setScan(nxt.id, s))
      .catch((e) => setScan(nxt.id, { status: "error", message: errMsg(e, "We couldn't read this document. You can enter the details yourself.") }))
      .finally(() => setScanning(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [papers, scanning]);
  const warn = papers.filter((m) => m.scan && m.scan.status !== "recognized");
  const retry = (m) => { if (scanning) return; tried.current.delete(m.id); setScan(m.id, undefined); };
  const replaceRef = useRef(null);
  const [replacing, setReplacing] = useState(null);
  const replace = async (files) => {
    const old = replacing; setReplacing(null);
    if (!files?.length || !old) return;
    await q.addFiles(files);
    remove(old.id);
  };
  return (
    <section data-testid="papers-section" className="rounded-xl border border-stone-200 bg-white/60 p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-stone-900 flex items-center gap-2"><FileText className="h-5 w-5 text-amber-800" /> Upload Horse Registration Papers</h3>
        <span data-testid="papers-count" className="text-xs text-stone-500 whitespace-nowrap">{papers.length} / {company.max_papers || 10}</span>
      </div>
      <p className="text-sm text-stone-600 mb-3">Photos (front and back) or a PDF of the papers. We'll read them to fill in your horse's details. Kept private until your ad is approved and posted.</p>
      <DropZone testId="papers" accept={`${PHOTO_ACCEPT},application/pdf,.pdf`} onFiles={q.addFiles} disabled={q.full} icon={FileText}
        label={q.full ? "Limit reached" : "Add Registration Papers"} dropLabel="Drop Papers Here" />
      {q.notice && <p data-testid="papers-notice" role="alert" className="mt-2 text-sm font-medium text-red-600">{q.notice}</p>}
      {(papers.length > 0 || q.queue.length > 0) && (
        <div data-testid="papers-grid" className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {papers.map((m) => <PaperTile key={m.id} m={m} app={app} token={token} scanning={scanning === m.id} onRemove={() => remove(m.id)} removing={removing === m.id} />)}
          {q.queue.map((x) => <QueueTile key={x.key} x={x} onRetry={() => q.retry(x.key)} onDrop={() => q.drop(x.key)} />)}
        </div>
      )}
      {warn.map((m) => {
        const red = m.scan.status === "not_papers";
        return (
          <div key={m.id} data-testid={`paper-warn-${m.id}`} role="alert" className={`mt-2 rounded-md border p-2.5 text-sm space-y-2 ${red ? "border-red-300 bg-red-50 text-red-900" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
            <div className="flex gap-1.5"><AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /><span><strong>{m.original_filename || "Document"}:</strong> {red
              ? "This does not appear to be horse registration papers. It won't be used to fill in your horse's information."
              : "We couldn't confidently read your horse's registration papers. You can try another image or enter the information manually."}</span></div>
            <div className="flex flex-wrap gap-2">
              <button type="button" data-testid={`paper-retry-${m.id}`} disabled={!!scanning} onClick={() => retry(m)} className="h-11 px-4 rounded-full border border-current text-xs font-semibold disabled:opacity-40">Retry Scan</button>
              <button type="button" data-testid={`paper-replace-${m.id}`} disabled={!!scanning || !!q.queue.length} onClick={() => { setReplacing(m); replaceRef.current?.click(); }} className="h-11 px-4 rounded-full border border-current text-xs font-semibold disabled:opacity-40">Replace Document</button>
              {onManual && <button type="button" data-testid={`paper-manual-${m.id}`} onClick={onManual} className="h-11 px-4 rounded-full bg-primary text-white text-xs font-semibold">Continue Manually</button>}
            </div>
          </div>
        );
      })}
      <input ref={replaceRef} type="file" data-testid="paper-replace-input" accept={`${PHOTO_ACCEPT},application/pdf,.pdf`} className="hidden"
        onChange={(e) => { replace(Array.from(e.target.files || [])); e.target.value = ""; }} />
    </section>
  );
}

function PaperTile({ m, app, token, onRemove, removing, scanning }) {
  const [load] = useState(() => () => fetchDraftMediaBlob(app.id, token, m.id));
  const s = m.scan?.status;
  return (
    <Tile testId={`paper-${m.id}`} onRemove={onRemove} removing={removing}>
      {m.content_type === "application/pdf"
        ? <div className="h-full w-full flex flex-col items-center justify-center p-1 text-center"><FileText className="h-8 w-8 text-red-700" /><span className="text-[10px] truncate max-w-full">{m.original_filename}</span></div>
        : <BlobImg load={load} alt={m.original_filename} className="h-full w-full object-cover" />}
      <span data-testid={`paper-scan-${m.id}`} className={`absolute bottom-0 inset-x-0 px-1 py-0.5 text-[10px] leading-tight font-bold text-center ${scanning ? "bg-white text-stone-700" : s === "recognized" ? "bg-emerald-600 text-white" : s === "not_papers" ? "bg-red-600 text-white" : s ? "bg-amber-500 text-white" : "bg-white/90 text-stone-500"}`}>
        {scanning ? "AI scanning…" : s === "recognized" ? "✓ Registration Papers Recognized" : s === "not_papers" ? "✕ Does Not Appear to Be Registration Papers" : s ? "⚠ Unable to Confirm Registration Papers" : "Waiting to scan…"}
      </span>
    </Tile>
  );
}

export function MediaStep({ app, token, company, onMediaChange }) {
  usePreventWindowDrop();
  const photos = (app.media || []).filter((m) => m.kind === "photo");
  const mainId = (photos.find((m) => m.is_main) || photos[0] || {}).id;
  const { queue, notice, addFiles, retry, drop, full } = useUploadQueue({ app, token, kind: "photo", items: photos, max: company.max_photos, maxBytes: company.max_photo_bytes, noun: "photos", onMediaChange });
  const [removing, remove] = useRemove(app, token, onMediaChange);

  const persistOrder = async (list, main) => {
    onMediaChange((all) => [...list, ...all.filter((m) => m.kind !== "photo")].map((m) => (m.kind === "photo" ? { ...m, is_main: m.id === main } : m)));
    try {
      await saveMediaOrder(app.id, token, list.map((m) => m.id), main);
    } catch (e) {
      toast.error(errMsg(e, "Could not save photo order"));
    }
  };
  const move = (from, to) => {
    if (from === to || to < 0 || to >= photos.length) return;
    const list = [...photos];
    list.splice(to, 0, list.splice(from, 1)[0]);
    persistOrder(list, mainId);
  };
  const drag = useTileDrag(move);

  return (
    <div className="space-y-6">
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-stone-900 flex items-center gap-2"><Camera className="h-5 w-5 text-amber-800" /> Photos</h3>
          <span data-testid="photo-count" className="text-xs text-stone-500">{photos.length} / {company.max_photos}</span>
        </div>
        <p className="text-sm text-stone-600 mb-3">Clear photos of the horse: both sides, front, and any papers or brand. JPG, PNG or iPhone HEIC, up to 25 MB each.</p>
        <DropZone testId="photo" accept={PHOTO_ACCEPT} onFiles={addFiles} disabled={full} icon={Camera}
          label={full ? "Photo limit reached" : "Add Photos"} dropLabel="Drop Photos Here" />
        {notice && <p data-testid="photo-notice" role="alert" className="mt-2 text-sm font-medium text-red-600">{notice}</p>}
        {(photos.length > 0 || queue.length > 0) && (
          <>
            {photos.length > 1 && <p className="mt-3 text-xs text-stone-500"><span className="hidden md:inline">Drag photos to reorder, or use the arrows.</span><span className="md:hidden">Use the arrows to reorder.</span> Tap the star to choose the main photo. Not every photo has to appear in the final ad.</p>}
            <div data-testid="photo-grid" className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {photos.map((m, i) => (
                <PhotoTile key={m.id} m={m} i={i} total={photos.length} isMain={m.id === mainId} app={app} token={token} drag={drag}
                  onMove={move} onMain={() => persistOrder(photos, m.id)} onRemove={() => remove(m.id)} removing={removing === m.id}
                  onRotated={(r) => onMediaChange((list) => list.map((x) => (x.id === m.id ? { ...x, rotation: r } : x)))} />
              ))}
              {queue.map((x) => (
                <QueueTile key={x.key} x={x} onRetry={() => retry(x.key)} onDrop={() => drop(x.key)} />
              ))}
            </div>
          </>
        )}
      </section>
      <VideoUploader scope={{ type: "app", id: app.id, token }} />
    </div>
  );
}
